import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { colors } from '../../colors';
import type {
  BoxInputLayout,
  StyledBoxInputProps,
  StyledInputGroupProps,
  StyledLabelProps,
  StyledLinkButtonProps,
} from './types';

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return css`
        gap: 8px;
      `;
    case 'large':
      return css`
        gap: 16px;
      `;
    default:
      return css`
        gap: 12px;
      `;
  }
};

const getMarginBottomForSize = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return '8px';
    case 'large':
      return '16px';
    default:
      return '12px';
  }
};

const getGapForSize = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return '12px';
    case 'large':
      return '20px';
    default:
      return '16px';
  }
};

const getGridGapForSize = (size: 'small' | 'medium' | 'large', isCorners: boolean) => {
  if (isCorners) {
    switch (size) {
      case 'small':
        return '32px';
      case 'large':
        return '48px';
      default:
        return '40px';
    }
  } else {
    switch (size) {
      case 'small':
        return '8px';
      case 'large':
        return '16px';
      default:
        return '12px';
    }
  }
};

const getFontSizeForSize = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return '10px';
    case 'large':
      return '12px';
    default:
      return '11px';
  }
};

const getLabelSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return css`
        font-size: 12px;
        font-weight: 500;
      `;
    case 'large':
      return css`
        font-size: 16px;
        font-weight: 600;
      `;
    default:
      return css`
        font-size: 14px;
        font-weight: 500;
      `;
  }
};

const getLinkButtonSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return css`
        width: 28px;
        height: 28px;

        > svg {
          width: 14px;
          height: 14px;
        }
      `;
    case 'large':
      return css`
        width: 36px;
        height: 36px;

        > svg {
          width: 20px;
          height: 20px;
        }
      `;
    default:
      return css`
        width: 32px;
        height: 32px;

        > svg {
          width: 16px;
          height: 16px;
        }
      `;
  }
};

export const Container = styled.div<StyledBoxInputProps>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: fit-content;

  ${({ $size }) => getSizeStyles($size)}

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

export const Header = styled.div<StyledInputGroupProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${({ $size }) => getMarginBottomForSize($size)};
`;

export const Title = styled.h3<StyledLabelProps>`
  margin: 0;
  color: ${({ $styleProps }) => $styleProps?.labelColor ?? colors.gray[800]};
  ${({ $size }) => getLabelSizeStyles($size)}
`;

export const LinkButton = styled.button<StyledLinkButtonProps>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ $styleProps, $linked }) => {
      if ($linked) {
        return $styleProps?.linkedButtonBorderColor ?? colors.primary[300];
      }

      return $styleProps?.linkButtonBorderColor ?? colors.gray[200];
    }};
  background: ${({ $linked, $styleProps }) => {
    if ($linked) {
      return $styleProps?.linkedButtonBackgroundColor ?? colors.primary[300];
    }
    return $styleProps?.linkButtonBackgroundColor ?? colors.white;
  }};
  color: ${({ $linked, $styleProps }) => {
    if ($linked) {
      return $styleProps?.linkedButtonTextColor ?? colors.white;
    }
    return $styleProps?.linkButtonTextColor ?? colors.gray[600];
  }};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  ${({ $size }) => getLinkButtonSizeStyles($size)}

  &:hover:not(:disabled) {
    background: ${({ $linked, $styleProps }) =>
      $linked
        ? ($styleProps?.linkedButtonHoverBackgroundColor ?? colors.primary[300])
        : ($styleProps?.linkButtonHoverBackgroundColor ?? colors.gray[100])};
    border-color: ${({ $linked, $styleProps }) =>
      $linked
        ? ($styleProps?.linkedButtonHoverBackgroundColor ?? colors.primary[300])
        : ($styleProps?.linkButtonHoverBackgroundColor ?? colors.gray[300])};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${colors.gray[100]};
  }

  &:focus {
    outline: 1px solid ${colors.primary[300]};
    outline-offset: 2px;
  }
`;

export const Content = styled.div<StyledInputGroupProps>`
  display: flex;
  gap: ${({ $size }) => getGapForSize($size)};
`;

export const InputsGrid = styled.div<StyledInputGroupProps>`
  display: grid;
  align-items: center;
  justify-items: center;
  flex: 1;
  position: relative;

  ${({ $layout, $size }) =>
    $layout === 'corners'
      ? css`

          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto auto;
          gap: ${getGridGapForSize($size, true)};
        `
      : css`

          grid-template-columns: 1fr auto 1fr;
          grid-template-rows: auto 1fr auto;
          gap: ${getGridGapForSize($size, false)};
        `}
`;

export const CenterButton = styled.div<StyledInputGroupProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  margin-top: 12px;
`;

export const InputWrapper = styled.div<{
  $position: 'top' | 'right' | 'bottom' | 'left';
  $layout: BoxInputLayout;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 160px;

  ${({ $position, $layout }) => {
    if ($layout === 'corners') {

      switch ($position) {
        case 'top':
          return css`
            grid-column: 1;
            grid-row: 1;
          `;
        case 'right':
          return css`
            grid-column: 2;
            grid-row: 1;
          `;
        case 'bottom':
          return css`
            grid-column: 2;
            grid-row: 2;
          `;
        case 'left':
          return css`
            grid-column: 1;
            grid-row: 2;
          `;
      }
    } else {

      switch ($position) {
        case 'top':
          return css`
            grid-column: 2;
            grid-row: 1;
          `;
        case 'right':
          return css`
            grid-column: 3;
            grid-row: 2;
          `;
        case 'bottom':
          return css`
            grid-column: 2;
            grid-row: 3;
          `;
        case 'left':
          return css`
            grid-column: 1;
            grid-row: 2;
          `;
      }
    }
  }}
`;

export const InputLabel = styled.label<StyledLabelProps>`
  color: ${({ $styleProps }) => $styleProps?.labelColor ?? colors.gray[600]};
  font-size: ${({ $size }) => getFontSizeForSize($size)};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;